import React, { useRef, useEffect, useState } from "react";
import styled from "styled-components";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import LoadingSpinner from "../../../ui/components/LoadingSpinner";
import Colors from "../../../ui/global/Colors";
import { motion } from "framer-motion";

const Root3D = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
    
    canvas {
        width: 100%;
        height: 100%;
        display: block;
    }
`;

const MiniViewsContainer = styled.div`
    position: absolute;
    top: 6px;
    right: 6px;
    display: flex;
    flex-direction: column;
    gap: 5px;
    z-index: 10;
`;

const MiniViewWrapper = styled(motion.div)<{ $size: number }>`
    width: ${props => props.$size}px;
    height: ${props => props.$size}px;
    border-radius: 4px;
    border: 1px solid ${Colors.grey2};
    background-color: ${Colors.white};
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    position: relative;
`;

const ViewLabel = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(0, 0, 0, 0.4);
    color: white;
    font-size: 10px;
    padding: 2px 0;
    text-align: center;
`;

const LoadingSpinnerContainer = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100px;
    height: 100px;
    padding: 0;
    z-index: 10;
`;

interface Stage3DProps {
    model3D: string;
    miniSize: number;
}

const Stage3D: React.FC<Stage3DProps> = ({ 
    model3D,
    miniSize,
}) => {
    const showDebugHitbox = true;
    const containerRef = useRef<HTMLDivElement>(null);
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const sceneRef = useRef<THREE.Scene | null>(null);
    const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
    const controlsRef = useRef<OrbitControls | null>(null);
    const modelRef = useRef<THREE.Object3D | null>(null);
    const boxHelperRef = useRef<THREE.BoxHelper | null>(null);
    const frameIdRef = useRef<number>(0);
    
    // Mini view refs
    const frontViewRef = useRef<HTMLDivElement>(null);
    const sideViewRef = useRef<HTMLDivElement>(null);
    const backViewRef = useRef<HTMLDivElement>(null);
    const frontRendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const sideRendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const backRendererRef = useRef<THREE.WebGLRenderer | null>(null);
    
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // useEffect(() => {
    //     console.log('model3D:', model3D);
    // }, [model3D]);

    // Initialize Three.js scene
    useEffect(() => {
        if (!containerRef.current) return;

        // Create scene
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf0f0f0);
        sceneRef.current = scene;

        // Create camera
        const aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
        const camera = new THREE.PerspectiveCamera(45, aspect, 0.1, 1000);
        // Position camera for a more straight-on view with slight tilt
        camera.position.set(0, 3, 12); // Moved camera up from y=2 to y=3
        camera.lookAt(0, 0, 0); // Adjusted lookAt target to match new camera height
        cameraRef.current = camera;

        // Create renderer
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
        renderer.setPixelRatio(window.devicePixelRatio);
        containerRef.current.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        // Key light - main directional light
        const keyLight = new THREE.DirectionalLight(0xffffff, 6.0); // Increased intensity
        keyLight.position.set(3, 5, 5); // Adjusted position to better illuminate front
        keyLight.castShadow = true;
        keyLight.shadow.mapSize.width = 2048;
        keyLight.shadow.mapSize.height = 2048;
        scene.add(keyLight);
        
        // Ambient light = soft light
        const ambientLight = new THREE.AmbientLight(0xffffff, 10.0);
        scene.add(ambientLight);

        // Fill light - soft light to reduce contrast
        const fillLight = new THREE.DirectionalLight(0xffffff, 8.0); 
        fillLight.position.set(3, 2, 5);
        scene.add(fillLight);

        // Rim light - backlight to highlight edges
        const rimLight = new THREE.DirectionalLight(0xffffff, 4.0);
        rimLight.position.set(-3, 5, -10);
        scene.add(rimLight);

        // Hemisphere light for more natural ambient
        const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0x444444, 6.0);
        scene.add(hemisphereLight);

        // Add orbit controls
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        
        controls.target.set(0, 2.5, 0); // Adjusted target to be slightly higher
        controlsRef.current = controls;

        // Add grid helper as ground plane
        const gridHelper = new THREE.GridHelper(10, 10);
        // Position grid slightly below y=0 to act as the ground
        gridHelper.position.y = -1;
        scene.add(gridHelper);

        // Animation loop
        const animate = () => {
            frameIdRef.current = requestAnimationFrame(animate);
            
            if (controlsRef.current) {
                controlsRef.current.update();
            }
            
            // Update box helper if it exists
            if (boxHelperRef.current && modelRef.current) {
                boxHelperRef.current.update();
            }
            
            if (rendererRef.current && sceneRef.current && cameraRef.current) {
                rendererRef.current.render(sceneRef.current, cameraRef.current);
            }
        };
        
        animate();

        // Handle window resize
        const handleResize = () => {
            if (!containerRef.current || !rendererRef.current || !cameraRef.current) return;
            
            const width = containerRef.current.clientWidth;
            const height = containerRef.current.clientHeight;
            
            cameraRef.current.aspect = width / height;
            cameraRef.current.updateProjectionMatrix();
            
            rendererRef.current.setSize(width, height);
        };
        
        window.addEventListener('resize', handleResize);

        // Cleanup function
        return () => {
            window.removeEventListener('resize', handleResize);
            cancelAnimationFrame(frameIdRef.current);
            
            if (rendererRef.current && containerRef.current) {
                containerRef.current.removeChild(rendererRef.current.domElement);
            }
            
            if (sceneRef.current) {
                // Dispose of scene resources
                sceneRef.current.traverse((object) => {
                    if (object instanceof THREE.Mesh) {
                        if (object.geometry) object.geometry.dispose();
                        
                        if (object.material) {
                            if (Array.isArray(object.material)) {
                                object.material.forEach(material => material.dispose());
                            } else {
                                object.material.dispose();
                            }
                        }
                    }
                });
            }
            
            if (rendererRef.current) {
                rendererRef.current.dispose();
            }
        };
    }, []);

    // Load 3D model when model3D changes
    useEffect(() => {
        if (!model3D || !sceneRef.current) return;
        
        // Remove previous model if exists
        if (modelRef.current && sceneRef.current) {
            sceneRef.current.remove(modelRef.current);
            modelRef.current = null;
        }
        
        setIsLoading(true);
        setError(null);
        
        const loader = new GLTFLoader();
        
        loader.load(
            model3D,
            (gltf) => {
                const model = gltf.scene;
                
                // Calculate initial model dimensions
                const initialBox = new THREE.Box3().setFromObject(model);
                const center = initialBox.getCenter(new THREE.Vector3());
                const size = initialBox.getSize(new THREE.Vector3());
                
                // Scale model to fill more of the viewport
                const maxDim = Math.max(size.x, size.y, size.z);
                const scale = 7 / maxDim;
                model.scale.set(scale, scale, scale);

                // Recalculate bounding box after scaling
                const scaledBox = new THREE.Box3().setFromObject(model);
                
                // Reset position to center in all dimensions but place on ground
                model.position.x = -center.x * scale;
                model.position.y = -scaledBox.min.y - 1; // Place bottom on grid
                model.position.z = -center.z * scale;
                
                if (sceneRef.current) {
                    sceneRef.current.add(model);
                    modelRef.current = model;
                    
                    // Add debug hitbox visualization if enabled
                    if (showDebugHitbox) {
                        // Remove previous box helper if it exists
                        if (boxHelperRef.current) {
                            sceneRef.current.remove(boxHelperRef.current);
                        }
                        
                        // Create new box helper for the model
                        const boxHelper = new THREE.BoxHelper(model, 0xff0000); // Red wireframe
                        sceneRef.current.add(boxHelper);
                        boxHelperRef.current = boxHelper;
                    }
                }
                
                setIsLoading(false);
            },
            (xhr) => {
                // Progress callback
                // console.log(`${(xhr.loaded / xhr.total) * 100}% loaded`);
            },
            (error) => {
                console.error('Error loading model:', error);
                setError('Failed to load 3D model');
                setIsLoading(false);
            }
        );
        
        return () => {
            // Cleanup when model3D changes
            if (sceneRef.current) {
                if (modelRef.current) {
                    sceneRef.current.remove(modelRef.current);
                    modelRef.current = null;
                }
                
                if (boxHelperRef.current) {
                    sceneRef.current.remove(boxHelperRef.current);
                    boxHelperRef.current = null;
                }
            }
        };
    }, [model3D]);

    // Setup mini views when model is loaded
    useEffect(() => {
        if (!modelRef.current || !sceneRef.current) return;
        
        // Create mini view scenes without the hitbox
        const createMiniViewScene = () => {
            if (!sceneRef.current || !modelRef.current) return null;
            
            // Create a new scene for mini view
            const miniScene = new THREE.Scene();
            miniScene.background = sceneRef.current.background;
            
            // Clone lights from main scene
            sceneRef.current.children.forEach(child => {
                if (child instanceof THREE.Light || child instanceof THREE.GridHelper) {
                    miniScene.add(child.clone());
                }
            });
            
            // Add a strong directional light from behind to illuminate the back view
            const backLight = new THREE.DirectionalLight(0xffffff, 4.0);
            backLight.position.set(0, 5, -8); // Position behind the model
            miniScene.add(backLight);
            
            // Add fill lights from sides to reduce harsh shadows
            const backFillLight1 = new THREE.DirectionalLight(0xffffff, 2.0);
            backFillLight1.position.set(5, 3, -5); // Right side back
            miniScene.add(backFillLight1);
            
            const backFillLight2 = new THREE.DirectionalLight(0xffffff, 2.0);
            backFillLight2.position.set(-5, 3, -5); // Left side back
            miniScene.add(backFillLight2);
            
            // Add model to mini scene (without BoxHelper)
            if (modelRef.current) {
                const clonedModel = modelRef.current.clone();
                miniScene.add(clonedModel);
            }
            
            return miniScene;
        };
        
        // Setup mini view renderers
        const setupMiniView = (containerRef: React.RefObject<HTMLDivElement>, rendererRef: React.RefObject<THREE.WebGLRenderer>, cameraPosition: THREE.Vector3, label: string) => {
            if (!containerRef.current || !sceneRef.current || !modelRef.current) return;
            
            // Create renderer
            const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
            renderer.setSize(miniSize, miniSize);
            renderer.setPixelRatio(window.devicePixelRatio);
            containerRef.current.appendChild(renderer.domElement);
            // rendererRef.current = renderer; // WARNING
            
            // Create camera
            const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);
            camera.position.copy(cameraPosition);
            camera.lookAt(0, 2, 0); // Look at center of model
            
            // Create mini scene without hitbox
            const miniScene = createMiniViewScene();
            
            // Render the view
            if (miniScene) {
                renderer.render(miniScene, camera);
            }
            
            return () => {
                if (rendererRef.current && containerRef.current) {
                    containerRef.current.removeChild(rendererRef.current.domElement);
                    rendererRef.current.dispose();
                    // rendererRef.current = null; // WARNING
                }
            };
        };
        
        // Setup all mini views
        const cleanupFront = setupMiniView(frontViewRef, frontRendererRef, new THREE.Vector3(0, 2, 12), 'Front');
        const cleanupSide = setupMiniView(sideViewRef, sideRendererRef, new THREE.Vector3(12, 2, 0), 'Side');
        const cleanupBack = setupMiniView(backViewRef, backRendererRef, new THREE.Vector3(0, 2, -12), 'Back');
        
        // Update mini views when main view changes
        const updateMiniViews = () => {
            if (frontRendererRef.current) {
                const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);
                camera.position.set(0, 2, 12);
                camera.lookAt(0, 2, 0);
                const miniScene = createMiniViewScene();
                if (miniScene) {
                    frontRendererRef.current.render(miniScene, camera);
                }
            }
            
            if (sideRendererRef.current) {
                const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);
                camera.position.set(12, 2, 0);
                camera.lookAt(0, 2, 0);
                const miniScene = createMiniViewScene();
                if (miniScene) {
                    sideRendererRef.current.render(miniScene, camera);
                }
            }
            
            if (backRendererRef.current) {
                const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);
                camera.position.set(0, 2, -12);
                camera.lookAt(0, 2, 0);
                const miniScene = createMiniViewScene();
                if (miniScene) {
                    backRendererRef.current.render(miniScene, camera);
                }
            }
        };
        
        // Add to animation loop
        const originalAnimate = frameIdRef.current;
        cancelAnimationFrame(originalAnimate);
        
        const animate = () => {
            frameIdRef.current = requestAnimationFrame(animate);
            
            if (controlsRef.current) {
                controlsRef.current.update();
            }
            
            // Update box helper if it exists
            if (boxHelperRef.current && modelRef.current) {
                boxHelperRef.current.update();
            }
            
            if (rendererRef.current && sceneRef.current && cameraRef.current) {
                rendererRef.current.render(sceneRef.current, cameraRef.current);
                updateMiniViews();
            }
        };
        
        animate();
        
        return () => {
            cancelAnimationFrame(frameIdRef.current);
            if (cleanupFront) cleanupFront();
            if (cleanupSide) cleanupSide();
            if (cleanupBack) cleanupBack();
        };
    }, [modelRef.current]);

    return (
        <Root3D ref={containerRef}>
            {isLoading && (
                <LoadingSpinnerContainer>
                    <LoadingSpinner size="medium" />
                </LoadingSpinnerContainer>
            )}
            {error && <div style={{ position: 'absolute', top: '10px', left: '10px', background: 'rgba(255,0,0,0.5)', color: 'white', padding: '5px 10px', borderRadius: '4px' }}>{error}</div>}
            
            {model3D !== '' && !isLoading && (
                <MiniViewsContainer>
                    <MiniViewWrapper 
                        ref={frontViewRef}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                        $size={miniSize}
                    >
                        {/* <ViewLabel>Front</ViewLabel> */}
                    </MiniViewWrapper>
                    <MiniViewWrapper 
                        ref={sideViewRef}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                        $size={miniSize}
                    >
                        {/* <ViewLabel>Side</ViewLabel> */}
                    </MiniViewWrapper>
                    <MiniViewWrapper 
                        ref={backViewRef}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                        $size={miniSize}
                    >
                        {/* <ViewLabel>Back</ViewLabel> */}
                    </MiniViewWrapper>
                </MiniViewsContainer>
            )}
        </Root3D>
    );
};

export default Stage3D;


